import { createSignal, For, Show } from 'solid-js';
import { Portal } from 'solid-js/web';
import ColorPicker from '../utilities/ColorPicker';
import {
  PERFORMANCE_TARGET_COLOR_OPTIONS,
} from '../../utils/performanceTargetColor';

export interface TargetSelectionModalProps {
  show: boolean;
  onClose: () => void;
  availableTargets: string[];
  selectedTargetName: string;
  onSelectedTargetNameChange: (name: string) => void;
  targetColor: string;
  onTargetColorChange: (color: string) => void;
  onConfirm: () => void;
  confirmLabel?: string;
}

export default function TargetSelectionModal(props: TargetSelectionModalProps) {
  const [showColorPicker, setShowColorPicker] = createSignal(false);

  const handleClose = () => {
    setShowColorPicker(false);
    props.onClose();
  };

  const handleConfirm = () => {
    setShowColorPicker(false);
    props.onConfirm();
  };

  return (
    <Show when={props.show}>
      <Portal mount={typeof document !== 'undefined' ? (document.getElementById('main-content') || document.body) : undefined}>
        <div class="pagesettings-overlay" onClick={handleClose}>
          <div
            class="pagesettings-modal target_modal"
            onClick={(e) => e.stopPropagation()}
            style={{ position: 'relative', 'z-index': 10001 }}
          >
            <div
              class="flex justify-between items-center p-4 border-b"
              style="border-color: var(--color-border-primary);"
            >
              <h2 class="text-lg font-semibold" style="color: var(--color-text-primary);">
                Select Target
              </h2>
              <button
                type="button"
                onClick={handleClose}
                class="text-gray-500 hover:text-gray-700 transition-colors"
                style="color: var(--color-text-secondary);"
                aria-label="Close"
              >
                <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <div class="p-6">
              <Show
                when={props.availableTargets.length > 0}
                fallback={<p style="color: var(--color-text-primary);">No targets available</p>}
              >
                <select
                  value={props.selectedTargetName}
                  onChange={(e) => props.onSelectedTargetNameChange(e.currentTarget.value)}
                  class="target-modal-select"
                >
                  <For each={props.availableTargets}>
                    {(target) => <option value={target}>{target}</option>}
                  </For>
                </select>

                <div class="target-modal-footer">
                  <div class="target-modal-color-control">
                    <span class="target-modal-color-label">Target color</span>
                    <button
                      type="button"
                      class="target-modal-color-swatch"
                      style={{ 'background-color': props.targetColor }}
                      aria-label="Choose target color"
                      onClick={() => setShowColorPicker((open) => !open)}
                    />
                    <Show when={showColorPicker()}>
                      <div class="target-modal-color-picker-popover">
                        <ColorPicker
                          colors={[...PERFORMANCE_TARGET_COLOR_OPTIONS]}
                          onSelect={(color) => {
                            props.onTargetColorChange(color);
                            setShowColorPicker(false);
                          }}
                        />
                      </div>
                    </Show>
                  </div>

                  <div class="target-modal-actions">
                    <button
                      type="button"
                      onClick={handleClose}
                      class="px-4 py-2 text-sm rounded-md transition-colors"
                      style="background-color: var(--color-bg-button-secondary); color: var(--color-text-button-secondary);"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleConfirm}
                      class="px-4 py-2 text-sm rounded-md transition-colors font-medium"
                      style={`background-color: ${props.selectedTargetName && props.selectedTargetName !== 'No Target' ? '#16a34a' : 'var(--color-bg-button)'}; color: var(--color-text-inverse);`}
                    >
                      {props.confirmLabel ?? 'Select'}
                    </button>
                  </div>
                </div>
              </Show>
            </div>
          </div>
        </div>
      </Portal>
    </Show>
  );
}
